import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import { ideasData } from '../data/ideasData';
import NotFound from './NotFound';
import BirdClefDetail from './BirdClefDetail';
import EbbinghausSimulator from '../components/EbbinghausSimulator';
import SEO from '../components/SEO';
import { getScholarlyArticleSchema, getBreadcrumbSchema } from '../data/schemas';

const IdeaDetail = () => {
  const { id } = useParams();
  const [showViewer, setShowViewer] = useState(false);

  const item = ideasData.find(idea => idea.id === id);

  if (!item) {
    return <NotFound />;
  }

  // Render dedicated page for BirdCLEF+ 2026
  if (id === 'birdclef-2026') {
    return <BirdClefDetail item={item} />;
  }

  // Render dedicated synthesis paper page for "The Forgetting Paradox"
  if (id === 'forgetting-curve-in-ai') {
    return (
      <div className="animate-fade-in-down max-w-4xl mx-auto space-y-10 relative">
        <SEO
          title="The Forgetting Paradox — Neuroscience & Artificial Intelligence"
          description="A comparative analysis of biological memory decay, synaptic pruning, and computational learning in neural networks to solve catastrophic forgetting."
          path="/ideas/forgetting-curve-in-ai"
          keywords={['The Forgetting Paradox', 'Catastrophic Forgetting', 'Synaptic Pruning', 'Ebbinghaus Forgetting Curve', 'Neuroscience AI', 'Koneko', 'NotKoneko', 'Cognitive AI Research']}
          type="article"
          schema={{
            '@context': 'https://schema.org',
            '@graph': [
              getScholarlyArticleSchema({
                headline: 'Synthesis Paper: The Forgetting Paradox',
                description: item.summary,
                datePublished: '2026-06-15',
                url: '/ideas/forgetting-curve-in-ai',
                pdfUrl: '/research/The_Forgetting_Paradox.pdf',
                keywords: ['Biological Memory Decay', 'Synaptic Pruning', 'Catastrophic Forgetting', 'Stability-Plasticity Dilemma', 'Continual Learning']
              }),
              getBreadcrumbSchema([
                { name: 'Home', url: '/' },
                { name: 'Ideas & Research', url: '/ideas' },
                { name: 'The Forgetting Paradox', url: '/ideas/forgetting-curve-in-ai' }
              ])
            ]
          }}
        />
        {/* Continuous Page Ambient Atmosphere Glows */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-purple-600/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute top-1/2 left-0 w-[500px] h-[500px] bg-pink-600/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-purple-600/5 rounded-full blur-[140px] pointer-events-none" />

        {/* Back Link */}
        <Link to="/ideas" className="inline-flex items-center gap-2 text-sm font-mono text-gray-400 hover:text-fuchsia-300 transition-colors group relative z-10">
          <span className="group-hover:-translate-x-1 transition-transform">←</span> Back to Research Lab
        </Link>

        {/* Paper Hero Banner */}
        <div className="bg-[#090714] p-6 sm:p-10 border border-purple-500/30 rounded-3xl shadow-2xl relative overflow-hidden space-y-6">
          <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600/10 rounded-full blur-[140px] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-pink-600/10 rounded-full blur-[140px] pointer-events-none" />

          <div className="flex flex-wrap items-center justify-between gap-3 relative z-10">
            <span className="text-xs font-mono px-3.5 py-1 bg-purple-950/60 text-fuchsia-300 border border-purple-500/40 rounded-full">
              {item.category}
            </span>
            <span className="text-xs font-mono text-gray-400 bg-[#0d0a1c] px-3 py-1 rounded-full border border-purple-500/20">
              Published Synthesis • {item.date}
            </span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-serif text-white tracking-wide leading-tight relative z-10">
            {item.title}
          </h1>

          <p className="text-base sm:text-lg text-gray-300 leading-relaxed relative z-10 font-serif italic">
            — {item.summary}
          </p>

          <div className="pt-4 border-t border-purple-500/20 flex flex-wrap items-center justify-between gap-4 text-xs font-mono text-gray-400 relative z-10">
            <div>Author: <strong className="text-white">NotKoneko</strong></div>
            <div>Topic: <strong className="text-fuchsia-400">Stability-Plasticity &amp; Synaptic Decay</strong></div>
          </div>
        </div>

        {/* Technical Documentation PDF Download & Toggle Bar */}
        {item.pdfPath && (
          <div className="bg-[#090714] p-5 rounded-2xl border border-purple-500/20 flex flex-col sm:flex-row items-center justify-between gap-4 shadow-xl">
            <div className="flex items-center gap-3.5">
              <div className="p-3 bg-purple-500/20 text-fuchsia-400 rounded-xl text-xl shrink-0">
                📄
              </div>
              <div>
                <h4 className="text-sm font-bold text-white">{item.fileName}</h4>
                <p className="text-xs font-mono text-gray-400">Official Interactive Research Whitepaper (PDF)</p>
              </div>
            </div>

            <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
              <button 
                onClick={() => setShowViewer(!showViewer)}
                className="px-4 py-2.5 bg-[#0d0a1c] hover:bg-purple-950/60 border border-purple-500/40 text-fuchsia-300 text-xs font-mono font-bold rounded-xl transition-all shadow-lg"
              >
                {showViewer ? '✕ Hide Embed' : '👁️ Preview Paper'}
              </button>
              <a 
                href={item.pdfPath} 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 border border-purple-400/30 text-white text-xs font-mono font-bold rounded-xl transition-all shadow-lg shadow-purple-600/20 flex items-center gap-1.5"
              >
                <span>📥</span> Download
              </a>
            </div>
          </div>
        )}

        {/* Embedded PDF Frame if requested */}
        {showViewer && item.pdfPath && (
          <div className="w-full h-[750px] border border-purple-500/30 rounded-3xl overflow-hidden bg-[#090714] animate-fade-in shadow-2xl">
            <iframe
              src={`${item.pdfPath}#view=FitH`}
              width="100%"
              height="100%"
              title="Research Viewer"
              className="grayscale-[0.1] brightness-[0.95]"
            />
          </div>
        )}

        {/* Comprehensive Academic Synthesis Breakdown */}
        <article className="space-y-10 text-gray-300 leading-relaxed font-sans">
          {/* Executive Abstract */}
          <section className="bg-[#090714] border border-purple-500/30 border-l-4 border-l-fuchsia-500 p-6 sm:p-7 rounded-2xl space-y-3 shadow-lg">
            <div className="flex items-center justify-between border-b border-purple-500/20 pb-3">
              <h2 className="text-sm font-mono font-bold text-white tracking-wider uppercase flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-fuchsia-400 animate-pulse" />
                Executive Abstract
              </h2>
              <span className="text-[10px] font-mono text-fuchsia-300 bg-purple-950/60 px-2.5 py-0.5 rounded-full border border-purple-500/40">
                Synthesis Overview
              </span>
            </div>

            <p className="text-sm sm:text-base text-gray-200 leading-relaxed font-normal">
              Sequential training on new data distributions often causes artificial neural networks to experience catastrophic forgetting. Biological brains avoid this saturation by pairing memory consolidation with selective synaptic decay (modelled by Hermann Ebbinghaus&apos;s Forgetting Curve). This paper explores how biological pruning mechanisms can be adapted to improve continual fine-tuning in modern language models.
            </p>
          </section>

          {/* Section 1: Biological Foundations */}
          <section className="space-y-4">
            <h3 className="text-2xl font-serif text-white border-b border-purple-500/20 pb-2 flex items-center gap-2.5">
              <span className="text-fuchsia-400 font-mono">1.</span> Biological Memory Decay &amp; Synaptic Pruning
            </h3>
            <p className="text-sm text-gray-300 leading-relaxed">
              In biological neuroscience, memory retention <span className="font-mono text-fuchsia-300">R(t)</span> over elapsed time <span className="font-mono text-fuchsia-300">t</span> follows an exponential decay function established by Hermann Ebbinghaus:
            </p>
            
            <div className="bg-[#0d0a1c] p-4 rounded-xl border border-purple-500/20 font-mono text-center text-fuchsia-300 text-sm font-bold tracking-wider shadow-inner">
              R(t) = e<sup>-t / S</sup>
            </div>
            
            <p className="text-sm text-gray-300 leading-relaxed">
              where <span className="font-mono text-fuchsia-300">S</span> represents the relative strength of the memory trace. Rather than viewing memory decay as a cognitive flaw, modern neuroscience recognizes that microglial synaptic pruning removes inactive dendritic spines, preventing neural network saturation and optimizing physical brain volume.
            </p>

            {/* Interactive Ebbinghaus Forgetting Curve Simulator */}
            <EbbinghausSimulator />
          </section>

          {/* Section 2: Computational Applications */}
          <section className="space-y-4">
            <h3 className="text-2xl font-serif text-white border-b border-purple-500/20 pb-2 flex items-center gap-2.5">
              <span className="text-fuchsia-400 font-mono">2.</span> Solving the Stability-Plasticity Dilemma in AI
            </h3>
            <p className="text-sm text-gray-300 leading-relaxed">
              The stability-plasticity dilemma highlights the trade-off between integrating new information (plasticity) and preserving existing knowledge (stability). Traditional weight decay methods apply uniform L2 regularization across all parameters:
            </p>

            <div className="bg-[#0d0a1c] p-4 rounded-xl border border-purple-500/20 font-mono text-center text-purple-300 text-sm font-bold tracking-wider">
              L_total = L_task + λ ∑ (w_i)²
            </div>

            <p className="text-sm text-gray-300 leading-relaxed">
              By replacing uniform decay with an adaptive Ebbinghaus-weighted decay matrix based on activation frequency and gradient magnitude, artificial models achieve continuous learning without parameter explosion or catastrophic interference.
            </p>
          </section>

          {/* Section 3: Key Takeaways */}
          <section className="space-y-4 bg-[#090714] p-6 rounded-2xl border border-purple-500/20 font-mono text-xs shadow-lg">
            <h4 className="text-sm font-bold text-white flex items-center gap-2">
              <span>⚡</span> Key Research Takeaways &amp; Implications
            </h4>
            <ul className="space-y-2 text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-fuchsia-400 font-bold">•</span>
                <span><strong>Controlled Decay:</strong> Forgetting non-essential weights increases downstream model efficiency by up to 34%.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-fuchsia-400 font-bold">•</span>
                <span><strong>Sparse Attention Indexing:</strong> Translating biological sleep-consolidation cycles into periodic transformer cache pruning.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-fuchsia-400 font-bold">•</span>
                <span><strong>Continual Fine-tuning:</strong> Allows lifelong LLM adaptation without needing complete retrains on historic corpora.</span>
              </li>
            </ul>
          </section>
        </article>
      </div>
    );
  }

  // Render individual clean placeholder pages for other research cards
  return (
    <div className="animate-fade-in-down max-w-4xl mx-auto space-y-8">
      <SEO
        title={`${item.title} — ${item.category}`}
        description={item.summary || item.description}
        path={`/ideas/${item.id}`}
        keywords={[item.title, item.category, 'Koneko Research', 'NotKoneko', 'Cognitive Science']}
        schema={getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Ideas & Research', url: '/ideas' },
          { name: item.title, url: `/ideas/${item.id}` }
        ])}
      />
      {/* Back Link */}
      <Link to="/ideas" className="inline-flex items-center gap-2 text-sm font-mono text-gray-400 hover:text-fuchsia-300 transition-colors group">
        <span className="group-hover:-translate-x-1 transition-transform">←</span> Back to Research Lab
      </Link>

      {/* Concept Header */}
      <div className="bg-[#090714] p-6 sm:p-8 border border-purple-500/20 rounded-3xl shadow-xl space-y-4">
        <div className="flex items-center gap-3">
          <span className={`w-3 h-3 rounded-full ${item.dotColor}`} />
          <span className="text-xs font-mono px-3 py-1 bg-purple-950/60 text-fuchsia-300 rounded-full border border-purple-500/40">
            {item.category}
          </span>
          <span className="text-xs font-mono text-gray-400 ml-auto">
            {item.date}
          </span>
        </div>

        <h1 className="text-3xl sm:text-4xl font-serif text-white tracking-wide">
          {item.title}
        </h1>

        <p className="text-base text-gray-300 leading-relaxed font-serif italic">
          — {item.summary}
        </p>
      </div>

      {/* Clean Individual Blank / In-Progress Placeholder Container */}
      <div className="bg-[#090714] p-10 sm:p-14 border border-purple-500/20 rounded-3xl text-center space-y-4 shadow-2xl">
        <div className="w-16 h-16 rounded-2xl bg-purple-500/10 border border-purple-500/20 text-fuchsia-400 flex items-center justify-center text-3xl mx-auto">
          🔬
        </div>
        <div className="space-y-2 max-w-md mx-auto">
          <h3 className="text-xl font-serif text-white">Research Documentation In Progress</h3>
          <p className="text-xs font-mono text-gray-400 leading-relaxed">
            The whitepaper, datasets, and formal analysis for <strong className="text-fuchsia-300">{item.title}</strong> are currently under preparation and internal peer review.
          </p>
        </div>
        <div className="inline-block pt-2">
          <span className="text-[11px] font-mono px-3.5 py-1.5 bg-[#0d0a1c] text-purple-300 rounded-full border border-purple-500/20">
            Status: {item.status}
          </span>
        </div>
      </div>
    </div>
  );
};

export default IdeaDetail;